import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    HiChevronDown,
    HiChevronUp,
    HiOutlineSearch,
    HiSelector,
} from "react-icons/hi";

import { Box } from "../../layout/Boxes";
import Breadcrumbs from "../../layout/Breadcrumbs/Breadcrumbs";
import { Content, ContentTop } from "../../layout/Content";
import Pagination from "../../layout/Pagination";
import InviteUser from "./InviteUser";
import { formatDate, isDateInThePast } from "../../../helpers/utils";
import EditUserButton from "./EditUserForm";
import ResendInviteButton from "./ResendInvite";
import {
    filterUsers,
    siteLabels,
    SiteNames,
    sortUsers,
    SortDirection,
    UserSortKey,
} from "./userTable";

const PAGE_SIZE = 100;
const SITES_PREVIEW_COUNT = 3;

type PageUsersProps = {
    users: CoreUser[];
    siteNames: SiteNames;
    refetch: () => Promise<unknown>;
};

type SortableHeaderProps = {
    label: string;
    sortKey: UserSortKey;
    activeKey: UserSortKey;
    direction: SortDirection;
    onSort: (key: UserSortKey) => void;
    className?: string;
};

function SortableHeader({
    label,
    sortKey,
    activeKey,
    direction,
    onSort,
    className,
}: SortableHeaderProps) {
    const isActive = activeKey === sortKey;
    const Icon = !isActive
        ? HiSelector
        : direction === "asc"
          ? HiChevronUp
          : HiChevronDown;

    return (
        <th
            className={className}
            aria-sort={
                isActive
                    ? direction === "asc"
                        ? "ascending"
                        : "descending"
                    : undefined
            }
        >
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className="inline-flex items-center gap-1 font-semibold hover:text-sky-600"
            >
                {label}
                <Icon
                    className={`h-4 w-4 ${isActive ? "" : "text-gray-400"}`}
                />
            </button>
        </th>
    );
}

function SitesCell({
    user,
    siteNames,
}: {
    user: CoreUser;
    siteNames: SiteNames;
}) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    if (user.sites.length === 0) {
        return <span className="text-gray-400">—</span>;
    }

    const sorted = siteLabels(user, siteNames);
    const visible = expanded ? sorted : sorted.slice(0, SITES_PREVIEW_COUNT);
    const hiddenCount = sorted.length - visible.length;

    return (
        <div className="flex flex-wrap items-center gap-1">
            {visible.map((site) => (
                <span
                    key={site}
                    className="bg-gray-100 rounded px-1.5 py-0.5 text-xs font-medium"
                >
                    {site}
                </span>
            ))}
            {hiddenCount > 0 && (
                <button
                    type="button"
                    className="text-xs text-sky-600 hover:underline"
                    onClick={() => setExpanded(true)}
                >
                    {t("+{{n}} more", { n: hiddenCount })}
                </button>
            )}
            {expanded && sorted.length > SITES_PREVIEW_COUNT && (
                <button
                    type="button"
                    className="text-xs text-sky-600 hover:underline"
                    onClick={() => setExpanded(false)}
                >
                    {t("Show less")}
                </button>
            )}
        </div>
    );
}

export default function PageUsers({
    users,
    siteNames,
    refetch,
}: PageUsersProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState<UserSortKey>("email");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    const handleSort = (key: UserSortKey) => {
        if (key === sortKey) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDirection("asc");
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
        // A narrowed result set starts over from its first page.
        const params = new URLSearchParams(location.search);
        if (params.has("page")) {
            params.delete("page");
            navigate(
                { pathname: location.pathname, search: params.toString() },
                { replace: true }
            );
        }
    };

    const filtered = useMemo(
        () =>
            sortUsers(
                filterUsers(users, search, siteNames),
                sortKey,
                sortDirection,
                siteNames
            ),
        [users, search, sortKey, sortDirection, siteNames]
    );

    const pageParam = parseInt(
        new URLSearchParams(location.search).get("page") || "1",
        10
    );
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const page = Math.min(Math.max(pageParam || 1, 1), totalPages);
    const pageUsers = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const breadcrumbPaths = [
        { label: t("Home"), href: "/" },
        { label: t("Users") },
    ];

    return (
        <Content>
            <Breadcrumbs items={breadcrumbPaths} />

            <ContentTop h1={t("Users")}>
                <div className="border border-stone-300 rounded p-1 buttons mt-0">
                    <InviteUser refetch={refetch} />
                </div>
            </ContentTop>

            <Box>
                <div className="p-4 sm:max-w-sm">
                    <div className="relative">
                        <HiOutlineSearch
                            fontSize={20}
                            className="text-gray-400 absolute top-1/2 -translate-y-1/2 left-3"
                        />
                        <input
                            type="search"
                            value={search}
                            onChange={handleSearchChange}
                            placeholder={t("Type to filter users")}
                            className="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                        />
                    </div>
                </div>
                {/* Fixed layout: expanding a site list must not resize the
                    other columns. Email takes the remaining width. */}
                <table className="w-full table-fixed text-gray-700">
                    <thead>
                        <tr>
                            <SortableHeader
                                label={t("Email")}
                                sortKey="email"
                                activeKey={sortKey}
                                direction={sortDirection}
                                onSort={handleSort}
                            />
                            <SortableHeader
                                label={t("Role")}
                                sortKey="role"
                                activeKey={sortKey}
                                direction={sortDirection}
                                onSort={handleSort}
                                className="w-32 text-center"
                            />
                            <SortableHeader
                                label={t("Expiration Date")}
                                sortKey="expiresAt"
                                activeKey={sortKey}
                                direction={sortDirection}
                                onSort={handleSort}
                                className="w-44 text-center hidden md:table-cell"
                            />
                            <SortableHeader
                                label={t("Sites")}
                                sortKey="sites"
                                activeKey={sortKey}
                                direction={sortDirection}
                                onSort={handleSort}
                                className="w-80 hidden lg:table-cell"
                            />
                            <th className="w-48 text-center">{t("Actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageUsers.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="text-center text-gray-500 py-8"
                                >
                                    {t("No users found")}
                                </td>
                            </tr>
                        )}
                        {pageUsers.map((user) => (
                            <tr key={user.email}>
                                <td className="truncate">{user.email}</td>
                                <td className="text-center">{user.role}</td>
                                <td className="text-center hidden md:table-cell">
                                    <span
                                        className={
                                            isDateInThePast(user.expiresAt)
                                                ? "text-red-500 italic"
                                                : ""
                                        }
                                    >
                                        {formatDate(user.expiresAt)}
                                    </span>
                                </td>
                                <td className="hidden lg:table-cell">
                                    <SitesCell
                                        user={user}
                                        siteNames={siteNames}
                                    />
                                </td>
                                <td>
                                    <div className="flex justify-center gap-2">
                                        <EditUserButton
                                            user={user}
                                            refetch={refetch}
                                        />
                                        <ResendInviteButton
                                            user={user}
                                            refetch={refetch}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filtered.length > 0 && (
                    <Pagination
                        pageInfo={{
                            limit: PAGE_SIZE,
                            offset: (page - 1) * PAGE_SIZE,
                            totalCount: filtered.length,
                        }}
                    />
                )}
            </Box>
        </Content>
    );
}
